
import { supabase } from '@/integrations/supabase/client';
import { User } from '@/types/auth';
import { getLogger } from '@/utils/logger';

const log = getLogger({ module: 'AuthService' });

export const signUpUser = async (email: string, password: string, options: any = {}) => {
  log.info("Signing up user with email:", email, { 
    ageVerified: options.ageVerified, 
    termsAccepted: options.termsAccepted 
  });
  
  // First, check if user exists by trying to sign in
  try {
    log.info("Attempting to sign in first...");
    const signInResult = await signInUser(email, password);
    log.info("User exists, signed in successfully");
    return signInResult;
  } catch (error) {
    log.info("Sign in failed, attempting signup...");
    
    // If sign in fails, try to sign up
    const { data, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          age_verified: options.ageVerified || false,
          terms_accepted: options.termsAccepted || false
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });

    if (signUpError) {
      log.error("Signup error details:", { 
        message: signUpError.message, 
        status: signUpError.status, 
        code: signUpError.code,
        details: JSON.stringify(signUpError)
      });
      throw signUpError;
    }

    log.info("Signup successful, returning data", { 
      userId: data?.user?.id,
      userEmail: data?.user?.email,
      hasSession: !!data?.session
    });
    return data;
  }
};

export const signInUser = async (email: string, password: string) => {
  log.info("Signing in user with email:", email);
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  });

  if (error) {
    log.error("Sign in error:", { 
      message: error.message, 
      status: error.status, 
      code: error.code 
    });
    throw error;
  }
  
  log.info("Sign in successful", { 
    userId: data?.user?.id,
    hasSession: !!data?.session 
  });
  return data;
};

export const getCurrentSession = async () => {
  log.info("Getting current session");
  const { data, error } = await supabase.auth.getSession();
  
  if (error) {
    log.error("Get session error:", error);
    throw error;
  }
  
  if (!data.session) {
    log.info("No active session found");
    return null;
  }
  
  log.info("Active session retrieved", { 
    userId: data.session?.user?.id,
    sessionExpiry: data.session?.expires_at
  });
  return data.session;
};

export const refreshSession = async () => {
  log.info("Refreshing session");
  const { data, error } = await supabase.auth.refreshSession();
  
  if (error) {
    log.error("Session refresh error:", error);
    throw error;
  }
  
  if (!data.session) {
    log.info("No session after refresh");
    return null;
  }
  
  log.info("Session refreshed successfully", {
    userId: data.session?.user?.id,
    expiresAt: data.session?.expires_at
  });
  
  return data.session;
};

export const createUserData = async (authData: any, userInfo: any): Promise<User> => {
  log.info("Creating user data", { 
    userId: authData.user.id, 
    email: userInfo.email 
  });
  return {
    id: authData.user.id,
    email: userInfo.email,
    username: undefined, // Will be set later when user creates username
  };
};
